import { parseViewBoxBounds } from "./pathAgents";
import {
    collectPathPopulateGeometry,
    samplePointsAlongGeometryInViewport,
    withMountedPathSvg,
} from "./svgUtils";

export type PathAgentSpawnGuideSample = {
    x: number;
    y: number;
    angle: number;
};

/**
 * Sample positions along path-like geometry in an SVG (paths, polylines,
 * polygons, lines, rects), in root viewBox space. Caps the result length.
 */
function centerSamplesInTargetViewBox(
    samples: PathAgentSpawnGuideSample[],
    root: SVGSVGElement,
    targetViewBoxAttr: string,
): PathAgentSpawnGuideSample[] {
    const target = parseViewBoxBounds(targetViewBoxAttr.trim());
    if (!target || samples.length === 0) return samples;

    const vb = root.viewBox.baseVal;
    if (!(vb.width > 0 && vb.height > 0)) return samples;

    const guideCx = vb.x + vb.width / 2;
    const guideCy = vb.y + vb.height / 2;
    const tw = target.maxX - target.minX;
    const th = target.maxY - target.minY;
    const targetCx = target.minX + tw / 2;
    const targetCy = target.minY + th / 2;
    const dx = targetCx - guideCx;
    const dy = targetCy - guideCy;
    if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return samples;

    return samples.map((s) => ({ ...s, x: s.x + dx, y: s.y + dy }));
}

/**
 * @param centerInViewBoxAttr When set (e.g. Path Agents artboard `viewBox`),
 * translates samples so the guide SVG’s viewBox is centered in this rectangle.
 */
export function collectSpawnSamplesFromSvg(
    svgMarkup: string,
    sampleDistancePx: number,
    maxSamples: number,
    centerInViewBoxAttr?: string,
): PathAgentSpawnGuideSample[] {
    if (!svgMarkup.trim() || sampleDistancePx <= 0 || maxSamples <= 0) {
        return [];
    }
    const mounted = withMountedPathSvg(svgMarkup, (root) => {
        const segments = collectPathPopulateGeometry(root);
        const perSegment: PathAgentSpawnGuideSample[][] = [];
        for (const { el, isClosed } of segments) {
            const samples = samplePointsAlongGeometryInViewport(
                el,
                sampleDistancePx,
                isClosed,
            );
            if (samples.length > 0) {
                perSegment.push(samples);
            }
        }
        if (perSegment.length === 0) return [];

        // Balance across segments: round-robin one sample per segment per pass.
        // This avoids early document-order segments consuming the full cap.
        const out: PathAgentSpawnGuideSample[] = [];
        let cursor = 0;
        while (out.length < maxSamples) {
            let madeProgress = false;
            for (const samples of perSegment) {
                if (cursor < samples.length) {
                    out.push(samples[cursor]!);
                    madeProgress = true;
                    if (out.length >= maxSamples) break;
                }
            }
            if (!madeProgress) break;
            cursor += 1;
        }
        if (centerInViewBoxAttr?.trim()) {
            return centerSamplesInTargetViewBox(out, root, centerInViewBoxAttr);
        }
        return out;
    });
    if (!mounted || mounted.length === 0) return [];
    return mounted;
}
